import type { CarrierAndService } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-common/fulfillment-group-status";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  OutboundParcel,
  Parcel,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import { WeightChangeSource } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import type { DisplayParcel } from "../../utils/use-group-info";
import { ParcelInput } from "./parcel-input";
import { ParcelList } from "./parcel-list";
import { ParcelModal } from "./parcel-modal";
import { ParcelPackingSlipDownloadButton } from "./parcel-packing-slip-download-button";

export const ParcelDetailsHeaderActions = memo(
  function ParcelDetailsHeaderActions({
    allowMultipleParcels,
    isEditingLocked,
    onAddPackage,
  }: {
    allowMultipleParcels: boolean;
    isEditingLocked: boolean;
    onAddPackage: () => void;
  }) {
    if (!allowMultipleParcels) {
      return null;
    }

    return (
      <RedoButton
        disabled={isEditingLocked}
        hierarchy="tertiary"
        onClick={onAddPackage}
        size="sm"
        text="Add another package"
      />
    );
  },
);

export const ParcelDetails = memo(function ParcelDetailsComponent({
  displayedButtonAction,
  everythingDisabled,
  mixedParcels,
  onParcelChange,
  onParcelAdd,
  parcelValues,
  selectedParcels,
  selectedRate,
  selectedStatus,
  usedPackageLearning,
  usedWeightLearning,
  disableBulkParcelEditing,
  mixedWeight,
  onRemoveParcel,
  trackingNumbers,
  isEditingLocked,
  parcelModalOpen,
  onParcelModalClose,
  onParcelModalOpen,
  showParcelPackingSlipButtons,
  showSingleParcelTrackingNumber = false,
  fulfillmentGroupId,
  items,
  onAllocationsFlush,
}: {
  displayedButtonAction: FulfillmentGroupActions;
  everythingDisabled: boolean;
  mixedParcels: boolean;
  onParcelChange: (
    parcel: Partial<OutboundParcel>,
    index: number,
    source?: WeightChangeSource,
  ) => void;
  onParcelAdd: (parcel: OutboundParcel) => void;
  parcelValues: readonly Parcel[];
  selectedParcels: DisplayParcel[];
  selectedRate?: CarrierAndService | null;
  selectedStatus: FulfillmentGroupStatus | null | undefined;
  usedPackageLearning: boolean;
  usedWeightLearning: boolean;
  mixedWeight: boolean;
  disableBulkParcelEditing?: boolean;
  onRemoveParcel: (index: number) => void;
  trackingNumbers: string[] | undefined;
  isEditingLocked: boolean;
  parcelModalOpen: boolean;
  onParcelModalClose: () => void;
  onParcelModalOpen: () => void;
  showParcelPackingSlipButtons?: boolean;
  showSingleParcelTrackingNumber?: boolean;
  fulfillmentGroupId?: string;
  items: FulfillmentOrderLineItem[];
  onAllocationsFlush?: (
    allocations: Map<
      number,
      { externalId: string; sku: string; quantity: number }[]
    >,
  ) => void;
}) {
  if (disableBulkParcelEditing) {
    return (
      <Flex
        bgColor="secondary"
        borderColor="tertiary"
        borderStyle="solid"
        borderWidth="1px"
        p="xl"
        radius="md"
      >
        <Text color="tertiary" fontSize="sm" textAlign="center">
          Bulk package editing is not available for orders with multiple
          packages. Select a single order to edit packages.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex w="full">
      <Flex dir="column" gap="2xl" w="full">
        {selectedParcels && selectedParcels.length > 1 ? (
          <ParcelList
            fulfillmentGroupId={fulfillmentGroupId}
            onRemoveParcel={onRemoveParcel}
            openModal={onParcelModalOpen}
            selectedParcels={selectedParcels}
            selectedStatus={selectedStatus}
            showParcelPackingSlipButtons={showParcelPackingSlipButtons}
            trackingNumbers={trackingNumbers}
          />
        ) : (
          <>
            <ParcelInput
              displayedButtonAction={displayedButtonAction}
              everythingDisabled={everythingDisabled}
              key={selectedParcels?.[0]?._id}
              mixedParcels={mixedParcels}
              mixedWeight={mixedWeight}
              onAddPackage={onParcelModalOpen}
              onParcelChange={(parcel, source) =>
                onParcelChange(parcel, 0, source)
              }
              parcelValues={parcelValues}
              selectedParcel={selectedParcels?.[0] ?? null}
              selectedRate={selectedRate}
              selectedStatus={selectedStatus}
              trackingNumber={
                showSingleParcelTrackingNumber
                  ? trackingNumbers?.[0]
                  : undefined
              }
              usedPackageLearning={usedPackageLearning}
              usedWeightLearning={usedWeightLearning}
            />
            {showParcelPackingSlipButtons && fulfillmentGroupId && (
              <ParcelPackingSlipDownloadButton
                fulfillmentGroupId={fulfillmentGroupId}
                parcelIndex={0}
              />
            )}
          </>
        )}
      </Flex>

      <ParcelModal
        displayedButtonAction={displayedButtonAction}
        everythingDisabled={everythingDisabled}
        items={items}
        mixedParcels={mixedParcels}
        onAllocationsFlush={onAllocationsFlush}
        onModalClose={onParcelModalClose}
        onParcelAdd={onParcelAdd}
        onParcelChange={onParcelChange}
        onRemoveParcel={onRemoveParcel}
        open={parcelModalOpen}
        parcelValues={parcelValues}
        selectedParcels={selectedParcels}
        selectedRate={selectedRate}
        selectedStatus={selectedStatus}
        trackingNumbers={trackingNumbers}
        usedPackageLearning={usedPackageLearning}
        usedWeightLearning={usedWeightLearning}
      />
    </Flex>
  );
});
